import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import JwtService from "@/services/jwt.service";

/**
 * Integrazione SSO (OpenID Connect / Keycloak).
 *
 * Attivazione: variabile d'ambiente VUE_APP_OIDC_ENABLED=true.
 * Quando disattivata, l'app usa il login locale classico (nessun cambiamento).
 *
 * Il trucco per non toccare il resto dell'app: l'access token di Keycloak viene
 * copiato in localStorage["id_token"], la stessa chiave che axios/ApiService già
 * usano per il Bearer. Il rinnovo silenzioso aggiorna quella chiave.
 *
 * IMPORTANTE: la stessa chiave e' condivisa col login locale legacy. Se un utente
 * arriva su Gestipec gia' con un id_token (legacy, o di un altro utente SSO) in
 * localStorage, senza un controllo esplicito quel token "vince" e la sessione
 * Keycloak non viene mai verificata: e' cosi' che si finisce autenticati con
 * l'identita' sbagliata arrivando dal tile della Dashboard. init() risolve questo
 * facendo SEMPRE un controllo silenzioso con Keycloak all'avvio: se c'e' una
 * sessione IdP attiva, il suo token sovrascrive qualunque cosa ci fosse gia'.
 */
const enabled = process.env.VUE_APP_OIDC_ENABLED === "true";

let userManager = null;

function buildUserManager() {
    return new UserManager({
        authority: process.env.VUE_APP_OIDC_AUTHORITY,
        client_id: process.env.VUE_APP_OIDC_CLIENT_ID,
        redirect_uri: window.location.origin + "/auth/callback",
        post_logout_redirect_uri:
            process.env.VUE_APP_OIDC_POST_LOGOUT || window.location.origin + "/",
        silent_redirect_uri: window.location.origin + "/auth/silent",
        response_type: "code",
        scope: process.env.VUE_APP_OIDC_SCOPE || "openid profile email",
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
}

const OidcService = {
    get enabled() {
        return enabled;
    },

    /**
     * Ritorna una Promise che si risolve quando il controllo silenzioso iniziale è
     * terminato (con o senza sessione trovata). main.js aspetta questa Promise prima
     * di montare l'app, cosi' nessun componente legge id_token prima che sia
     * sincronizzato con l'eventuale sessione Keycloak attiva.
     */
    async init() {
        if (!enabled) return;
        userManager = buildUserManager();

        userManager.events.addUserLoaded((user) => {
            JwtService.saveToken(user.access_token);
        });
        userManager.events.addAccessTokenExpired(() => {
            // il rinnovo silenzioso ha fallito: si riparte dal login (SSO -> di solito trasparente)
            OidcService.login();
        });
        userManager.events.addSilentRenewError(() => {
            OidcService.login();
        });

        // /auth/callback e /auth/silent sono le pagine che gestiscono il ritorno da
        // Keycloak (la seconda dentro un iframe nascosto, aperto proprio da
        // signinSilent()). L'intero bundle (main.js) riparte da capo anche li',
        // quindi senza questa guardia il controllo qui sotto si richiamerebbe da
        // solo ricorsivamente: ogni iframe ne aprirebbe un altro all'infinito.
        // Su quelle route ci pensano gia' AuthCallback.vue / AuthSilent.vue.
        const path = window.location.pathname;
        if (path === "/auth/callback" || path === "/auth/silent") {
            return;
        }

        // La sessione Keycloak vince sempre su un id_token gia' presente (legacy o
        // di un altro utente SSO): se il cookie IdP e' attivo, risincronizza subito,
        // sovrascrivendo qualunque token ci fosse gia' in localStorage.
        try {
            const user = await userManager.signinSilent();
            if (user) {
                JwtService.saveToken(user.access_token);
            }
        } catch {
            // Nessuna sessione Keycloak attiva (o controllo silenzioso fallito):
            // si lascia l'eventuale token gia' presente (es. login locale legacy)
            // cosi' com'e' - la guardia del router gestira' il resto.
        }
    },

    /** Redirect verso Keycloak (login o SSO silenzioso lato IdP). */
    async login() {
        if (!userManager) return;
        await userManager.signinRedirect({
            state: { returnTo: window.location.pathname + window.location.search },
        });
    },

    /** Da chiamare sulla route /auth/callback. */
    async handleLoginCallback() {
        const user = await userManager.signinRedirectCallback();
        JwtService.saveToken(user.access_token);
        return user;
    },

    /** Da chiamare sulla route /auth/silent (iframe). */
    async handleSilentCallback() {
        await userManager.signinSilentCallback();
    },

    async logout() {
        JwtService.destroyToken();
        if (userManager) {
            await userManager.signoutRedirect();
        }
    },
};

export default OidcService;
